/**
 * ReviewItemCard — one animated accept/reject card. Kind-colored ramps
 * (violet Σ formulas, amber ☰ rules, sky capacity); error cards start
 * rejected with Accept disabled; rejected cards dim and shrink slightly.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { motion } from 'framer-motion';
import { Check, Gauge, ListChecks, Sigma, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { ReviewItem } from '../validate/review-items';
import ReviewFormulaBody from './ReviewFormulaBody';
import ReviewIssueBadges from './ReviewIssueBadges';
import ReviewRuleBody from './ReviewRuleBody';

const RAMP = {
  'formula-group': {
    border: 'border-l-violet-500/70',
    bg: 'bg-violet-500/5',
    icon: Sigma,
    iconColor: 'text-violet-500',
  },
  'rule-group': {
    border: 'border-l-amber-500/70',
    bg: 'bg-amber-500/5',
    icon: ListChecks,
    iconColor: 'text-amber-500',
  },
  'capacity-rule': {
    border: 'border-l-sky-500/70',
    bg: 'bg-sky-500/5',
    icon: Gauge,
    iconColor: 'text-sky-500',
  },
} as const;

export interface ReviewItemCardProps {
  item: ReviewItem;
  index: number;
  onAccept: (accepted: boolean) => void;
}

const ReviewItemCard: FC<ReviewItemCardProps> = ({ item, index, onAccept }) => {
  const ramp = RAMP[item.kind];
  const Icon = ramp.icon;
  const blocked = item.issues.some((issue) => issue.level === 'error');
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{
        opacity: item.accepted ? 1 : 0.55,
        y: 0,
        scale: item.accepted ? 1 : 0.985,
      }}
      transition={{ delay: index * 0.04, duration: 0.18 }}
      className={cn(
        'rounded-md border border-border/70 border-l-4 p-2.5',
        ramp.border,
        item.accepted ? ramp.bg : 'bg-muted/20 saturate-50',
        blocked && 'ring-1 ring-destructive/40',
      )}
      data-testid="review-item-card"
    >
      <div className="flex items-start gap-2">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', ramp.iconColor)} aria-hidden />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-semibold">{item.label}</span>
            {item.layer ? (
              <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {item.layer}
              </span>
            ) : null}
          </div>
          {item.kind === 'formula-group' ? (
            <ReviewFormulaBody item={item} />
          ) : (
            <ReviewRuleBody item={item} />
          )}
          <ReviewIssueBadges issues={item.issues} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant={item.accepted ? 'default' : 'outline'}
            size="icon"
            className="h-6 w-6"
            aria-label={`Accept ${item.label}`}
            disabled={blocked}
            title={blocked ? 'Fix in JSON and re-paste.' : 'Accept'}
            onClick={() => onAccept(true)}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={item.accepted ? 'outline' : 'destructive'}
            size="icon"
            className="h-6 w-6"
            aria-label={`Reject ${item.label}`}
            onClick={() => onAccept(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReviewItemCard;
